// Sales data layer (Module 09 §5, slice P3-A part 2). TanStack Query hooks
// over the generated api-client — doctrine 3: apps/web talks to the API only
// through `@assessly/api-client`, never a hand-rolled fetch. Every mutation
// sends a fresh Idempotency-Key (§5: "mutations require Idempotency-Key") and
// invalidates the lists it affects; every queryFn threads `{signal}` so an
// unmounted screen's read is actually cancelled.
//
// The off-cut class and margin-guard catalogs are NOT paginated (bounded
// definition sets, the module-05/07 catalog rule), so there is no cursor
// plumbing on them — a page-walk would be inventing a control the server does
// not offer. Customers and price lists ARE cursor pages.

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";
import { keepPageOnTurn } from "../../../../lib/cursor-pages";

type S = components["schemas"];

export type Customer = S["Customer"];
export type Customer360 = S["Customer360Response"];
export type CustomerContact = S["CustomerContact"];
export type ContactChannel = S["ContactChannel"];
export type CustomerDistributorProduct = S["CustomerDistributorProduct"];
export type ReliabilitySnapshot = S["CustomerReliabilitySnapshot"];
export type OpenArState = S["OpenArState"];
export type PriceList = S["PriceList"];
export type PriceListItem = S["PriceListItem"];
export type PriceListDetail = S["PriceListDetailResponse"];
export type PriceListItemsPut = S["PriceListItemsPutRequest"];
export type PriceListItemsReport = S["PriceListItemsPutResponse"];
export type OffcutClass = S["OffcutDiscountClass"];
export type MarginGuard = S["MarginGuard"];
export type PricingResolve = S["PricingResolveResponse"];
export type PricingResolveRequest = S["PricingResolveRequest"];
export type CustomerCreateRequest = S["CustomerCreateRequest"];
export type CustomerUpdateRequest = S["CustomerUpdateRequest"];
export type ContactCreateRequest = S["ContactCreateRequest"];
export type ContactUpdateRequest = S["ContactUpdateRequest"];
export type ChannelCreateRequest = S["ChannelCreateRequest"];
export type PriceListCreateRequest = S["PriceListCreateRequest"];
export type OffcutClassCreateRequest = S["OffcutDiscountClassCreateRequest"];
export type OffcutClassUpdateRequest = S["OffcutDiscountClassUpdateRequest"];
export type MarginGuardCreateRequest = S["MarginGuardCreateRequest"];
export type MarginGuardUpdateRequest = S["MarginGuardUpdateRequest"];

export type CustomersQuery = NonNullable<paths["/v1/customers"]["get"]["parameters"]["query"]>;
export type PriceListsQuery = NonNullable<paths["/v1/price-lists"]["get"]["parameters"]["query"]>;

// §2.9 overrides (slice P3-C-2, D-263) — the READ side lives here; the three
// writes sit beside the other review-seam mutations in quote-queries.ts.
export type CustomerPriceOverride = S["CustomerPriceOverride"];
export type CustomerPriceOverrideReview = S["CustomerPriceOverrideReview"];
export type CustomerOverridesQuery = NonNullable<
  paths["/v1/customers/{id}/price-overrides"]["get"]["parameters"]["query"]
>;
/** The three facts the override rows need about a product — off
 * `GET /v1/catalogue/products/{id}`, one read per distinct id on the page. */
export interface ProductRef {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly base_uom_id: string;
}

/** Reference reads share the production/inventory cache posture: five minutes
 * fresh, never retried into an error card (they decorate, not gate). */
const REF_OPTIONS = { staleTime: 5 * 60_000, gcTime: 30 * 60_000, retry: false } as const;

export const salesKeys = {
  all: ["sales"] as const,
  customers: (query: CustomersQuery, cursor: string | undefined) =>
    ["sales", "customers", query, cursor ?? null] as const,
  customerLists: () => ["sales", "customers"] as const,
  customer: (id: string) => ["sales", "customer", id] as const,
  reliability: (id: string) => ["sales", "reliability", id] as const,
  priceLists: (query: PriceListsQuery, cursor: string | undefined) =>
    ["sales", "price-lists", query, cursor ?? null] as const,
  priceListLists: () => ["sales", "price-lists"] as const,
  priceList: (id: string) => ["sales", "price-list", id] as const,
  offcutClasses: () => ["sales", "offcut-classes"] as const,
  marginGuards: () => ["sales", "margin-guards"] as const,
  members: () => ["sales", "ref", "members"] as const,
  overrides: (customerId: string, query: CustomerOverridesQuery, cursor: string | undefined) =>
    ["sales", "overrides", customerId, query, cursor ?? null] as const,
  overrideLists: (customerId: string) => ["sales", "overrides", customerId] as const,
  productRef: (id: string) => ["sales", "ref", "product", id] as const,
};

// ---------------------------------------------------------------------------
// customers (§4.1 / §4.2)
// ---------------------------------------------------------------------------

/** ONE PAGE of the §4.1 customer list. The list screen's hook and its CSV
 * export (D-289 ruling 1) both call this, so the export walks exactly the
 * request the screen makes: same route, same query spread, same cursor rule. */
export async function fetchCustomersPage(
  query: CustomersQuery,
  cursor: string | undefined,
  signal?: AbortSignal,
) {
  const res = await client.GET("/v1/customers", {
    ...(signal !== undefined ? { signal } : {}),
    params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
  });
  return unwrap(res, "Could not load the customers");
}

export function useCustomers(query: CustomersQuery, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: salesKeys.customers(query, cursor),
    enabled,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(salesKeys.customers(query, cursor)),
    queryFn: async ({ signal }) => fetchCustomersPage(query, cursor, signal),
  });
}

/** The §4.2 payload — ONE GET renders the header and every shipped tab (no
 * waterfall; this is what meets the <2 s mobile pull-up budget). */
export function useCustomer360(id: string) {
  return useQuery({
    queryKey: salesKeys.customer(id),
    enabled: id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/customers/{id}", { signal, params: { path: { id } } });
      return unwrap(res, "Could not load the customer");
    },
  });
}

/** §3.9 snapshot history — LAZY: fetched only when the formula popover opens
 * (`enabled`), never as part of the 360 paint. */
export function useReliabilityHistory(id: string, enabled: boolean) {
  return useQuery({
    queryKey: salesKeys.reliability(id),
    enabled: enabled && id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/customers/{id}/reliability", {
        signal,
        params: { path: { id } },
      });
      return unwrap(res, "Could not load the reliability history").items;
    },
  });
}

export interface CreatedCustomer {
  readonly customer: Customer;
  /** The `X-Sales-Warning` header off the 201, as its list of TYPED codes
   * (`NO_PRICE_LIST_FOR_CURRENCY` from §9.13, and from E2-2c on
   * `POSSIBLE_DUPLICATE`), client-localized per D-225 and empty when the
   * header is absent. Read BEFORE unwrap, because unwrap surrenders the
   * Response on the throw path only. */
  readonly warnings: readonly string[];
}

/** A warning code as the header may carry one: an upper-case wire code. A
 * token of any other shape is dropped rather than printed. */
const WARNING_CODE = /^[A-Z][A-Z0-9_]*$/u;

/**
 * The `X-Sales-Warning` header as its list of codes (spec 09 §3.13, §5's E2-2
 * row 18). Since P3-E2 PR E2-2c the header is a comma-separated list in a
 * fixed order, `NO_PRICE_LIST_FOR_CURRENCY` first. One code today parses as a
 * list of one, so this parser ships in E2-2a1's web wave, before the server
 * sends a list, and no build prints a raw list as one code. Whitespace around
 * a code is ignored, an empty member is dropped, a repeated code is kept once,
 * and a member that is not a wire code is dropped.
 */
export function parseSalesWarnings(header: string | null | undefined): readonly string[] {
  if (header === null || header === undefined) return [];
  const out: string[] = [];
  for (const member of header.split(",")) {
    const code = member.trim();
    if (WARNING_CODE.test(code) && !out.includes(code)) out.push(code);
  }
  return out;
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomerCreateRequest): Promise<CreatedCustomer> => {
      const res = await client.POST("/v1/customers", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      const warnings = parseSalesWarnings(res.response.headers.get("X-Sales-Warning"));
      const customer = unwrap(res, "Could not create the customer");
      return { customer, warnings };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.customerLists() }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: CustomerUpdateRequest }) => {
      const res = await client.PATCH("/v1/customers/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the customer");
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: salesKeys.customer(input.id) });
      void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
    },
  });
}

export function useArchiveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/customers/{id}/archive", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not archive the customer");
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: salesKeys.customer(id) });
      void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
    },
  });
}

// ---------------------------------------------------------------------------
// contacts + channels (§4.2 Contacts tab)
// ---------------------------------------------------------------------------

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { customerId: string; body: ContactCreateRequest }) => {
      const res = await client.POST("/v1/customers/{id}/contacts", {
        params: {
          path: { id: input.customerId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
        body: input.body,
      });
      return unwrap(res, "Could not add the contact");
    },
    onSuccess: (_data, input) =>
      void qc.invalidateQueries({ queryKey: salesKeys.customer(input.customerId) }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; customerId: string; body: ContactUpdateRequest }) => {
      const res = await client.PATCH("/v1/contacts/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the contact");
    },
    onSuccess: (_data, input) =>
      void qc.invalidateQueries({ queryKey: salesKeys.customer(input.customerId) }),
  });
}

export function useAddChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      contactId: string;
      customerId: string;
      body: ChannelCreateRequest;
    }) => {
      const res = await client.POST("/v1/contacts/{id}/channels", {
        params: {
          path: { id: input.contactId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
        body: input.body,
      });
      return unwrap(res, "Could not add the channel");
    },
    onSuccess: (_data, input) =>
      void qc.invalidateQueries({ queryKey: salesKeys.customer(input.customerId) }),
  });
}

export function useRemoveChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; customerId: string }) => {
      const res = await client.DELETE("/v1/channels/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not remove the channel");
    },
    onSuccess: (_data, input) =>
      void qc.invalidateQueries({ queryKey: salesKeys.customer(input.customerId) }),
  });
}

// ---------------------------------------------------------------------------
// distributor eligible list (§2.5 / §4.2 Pricing tab)
// ---------------------------------------------------------------------------

export function useAddDistributorProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { customerId: string; productId: string; note?: string }) => {
      const res = await client.POST("/v1/customers/{id}/distributor-products", {
        params: {
          path: { id: input.customerId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
        body: {
          product_id: input.productId,
          ...(input.note !== undefined && input.note.length > 0 ? { note: input.note } : {}),
        },
      });
      return unwrap(res, "Could not add the product");
    },
    onSuccess: (_data, input) =>
      void qc.invalidateQueries({ queryKey: salesKeys.customer(input.customerId) }),
  });
}

export function useRemoveDistributorProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { customerId: string; productId: string }) => {
      const res = await client.DELETE("/v1/customers/{id}/distributor-products/{productId}", {
        params: {
          path: { id: input.customerId, productId: input.productId },
          header: { "Idempotency-Key": newIdempotencyKey() },
        },
      });
      return unwrap(res, "Could not remove the product");
    },
    onSuccess: (_data, input) =>
      void qc.invalidateQueries({ queryKey: salesKeys.customer(input.customerId) }),
  });
}

// ---------------------------------------------------------------------------
// price lists (§4.3)
// ---------------------------------------------------------------------------

export function usePriceLists(query: PriceListsQuery, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: salesKeys.priceLists(query, cursor),
    enabled,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(salesKeys.priceLists(query, cursor)),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/price-lists", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the price lists");
    },
  });
}

/** Head + full item set (the part-1 added detail read; unpaginated like the
 * module-05/07 catalogs). `margin_preview_pct` arrives per item ONLY for
 * `sales.margin.read` holders — §7 omission style, never null/marker. */
export function usePriceList(id: string, enabled = true) {
  return useQuery({
    queryKey: salesKeys.priceList(id),
    enabled: enabled && id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/price-lists/{id}", { signal, params: { path: { id } } });
      return unwrap(res, "Could not load the price list");
    },
  });
}

export function useCreatePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PriceListCreateRequest) => {
      const res = await client.POST("/v1/price-lists", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the price list");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.priceListLists() }),
  });
}

/** §5 PUT — replaces the draft's item set wholesale (rows absent from the
 * payload are deleted; the screen states that). Answers the row-validation
 * report, which the caller renders in the module-02 grammar. */
export function usePutPriceListItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: PriceListItemsPut }) => {
      const res = await client.PUT("/v1/price-lists/{id}/items", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the items");
    },
    onSuccess: (_data, input) =>
      void qc.invalidateQueries({ queryKey: salesKeys.priceList(input.id) }),
  });
}

/** 409 VALIDITY_OVERLAP carries `conflicting_price_list_id` in the problem
 * extensions — the caller reads it off `RequestError.problem` and renders the
 * §4.3 inline conflict diff. */
export function useActivatePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/price-lists/{id}/activate", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not activate the price list");
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: salesKeys.priceList(id) });
      void qc.invalidateQueries({ queryKey: salesKeys.priceListLists() });
    },
  });
}

export function useArchivePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/price-lists/{id}/archive", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not archive the price list");
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: salesKeys.priceList(id) });
      void qc.invalidateQueries({ queryKey: salesKeys.priceListLists() });
    },
  });
}

// ---------------------------------------------------------------------------
// off-cut discount classes + margin guards (§4.3 editors)
// ---------------------------------------------------------------------------

export function useOffcutClasses(enabled = true) {
  return useQuery({
    queryKey: salesKeys.offcutClasses(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/offcut-discount-classes", { signal, params: {} });
      return unwrap(res, "Could not load the discount classes").items;
    },
  });
}

export function useCreateOffcutClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OffcutClassCreateRequest) => {
      const res = await client.POST("/v1/offcut-discount-classes", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the discount class");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.offcutClasses() }),
  });
}

export function useUpdateOffcutClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: OffcutClassUpdateRequest }) => {
      const res = await client.PATCH("/v1/offcut-discount-classes/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the discount class");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.offcutClasses() }),
  });
}

/** The guard read rides `sales.tier.manage` (§5 — a guard's threshold IS
 * margin intelligence), so callers pass `enabled` from that mirror and the
 * screen never fires a read it knows will 403 (D-088). */
export function useMarginGuards(enabled: boolean) {
  return useQuery({
    queryKey: salesKeys.marginGuards(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/margin-guards", { signal, params: {} });
      return unwrap(res, "Could not load the margin guards").items;
    },
  });
}

export function useCreateMarginGuard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MarginGuardCreateRequest) => {
      const res = await client.POST("/v1/margin-guards", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the margin guard");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.marginGuards() }),
  });
}

export function useUpdateMarginGuard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: MarginGuardUpdateRequest }) => {
      const res = await client.PATCH("/v1/margin-guards/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the margin guard");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.marginGuards() }),
  });
}

// ---------------------------------------------------------------------------
// pricing resolve (§3.2 dry run)
// ---------------------------------------------------------------------------

/** A DRY RUN: writes nothing, reserves nothing (§3.3), so it invalidates
 * nothing. Still a POST with an Idempotency-Key — §5's blanket mutation rule. */
export function useResolvePrice() {
  return useMutation({
    mutationFn: async (body: PricingResolveRequest) => {
      const res = await client.POST("/v1/pricing/resolve", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not resolve the price");
    },
  });
}

// ---------------------------------------------------------------------------
// §2.9 customer price overrides — the §4.2 Pricing tab's list (P3-C-2, D-263)
// ---------------------------------------------------------------------------

/** §5 GET /v1/customers/{id}/price-overrides — LAZY, like the effective list
 * and the reliability history beside it: fetched only while the Pricing tab
 * is open, never as part of the 360's one GET (§4.2's < 2 s budget). The
 * price fields arrive OMITTED for a reader without `sales.read_value` (§7,
 * D-225) — the panel renders that as a sentence, never as a blank. */
export function useCustomerOverrides(
  customerId: string,
  query: CustomerOverridesQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: salesKeys.overrides(customerId, query, cursor),
    enabled: enabled && customerId.length > 0,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(salesKeys.overrides(customerId, query, cursor)),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/customers/{id}/price-overrides", {
        signal,
        params: {
          path: { id: customerId },
          query: { ...query, ...(cursor !== undefined ? { cursor } : {}) },
        },
      });
      return unwrap(res, "Could not load the negotiated prices");
    },
  });
}

/**
 * Product id → {sku, name, base UoM} for rows that carry only `product_id`
 * (§2.9 stores the reference, not the name). ONE detail read per DISTINCT
 * product on the page — bounded by the page size, cached five minutes, never
 * retried into an error card — rather than a list read the endpoint cannot
 * answer by id: `GET /v1/catalogue/products` has no `ids` filter and its `q`
 * is a trigram search that would name the wrong SKU. Rows resolve through
 * `RefLabel`, so an unresolved product is the short id in a mono chip and
 * never a broken name.
 */
export function useProductIndex(ids: readonly string[]) {
  const results = useQueries({
    queries: ids.map((id) => ({
      ...REF_OPTIONS,
      queryKey: salesKeys.productRef(id),
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        unwrap(
          await client.GET("/v1/catalogue/products/{id}", { signal, params: { path: { id } } }),
          "product",
        ),
    })),
  });
  const byId = new Map<string, ProductRef>();
  let isPending = false;
  results.forEach((result, index) => {
    const id = ids[index];
    if (id === undefined) return;
    if (result.isPending) isPending = true;
    const product = result.data;
    if (product !== undefined) {
      byId.set(id, {
        id,
        sku: product.sku,
        name: product.name,
        base_uom_id: product.base_uom_id,
      });
    }
  });
  return { byId, isPending };
}

// ---------------------------------------------------------------------------
// owner names (§4.1 owner column) — the production useWorkerIndex clone
// ---------------------------------------------------------------------------

/**
 * Owner id → display name. ONE list read, not one per id, and ONLY when the
 * caller may make it: `GET /v1/members` is gated on `identity.view_members`,
 * which of the §4.1 audience only group_admin and facility_admin hold — sales,
 * finance and planner do not. Firing a call we know will 403 is the dead call
 * D-088 forbids, so `enabled` carries the role test (the `isAdmin` mirror) and
 * the rows fall back to the short id in a `<code>` chip through `RefLabel` —
 * an identifier, never a broken name.
 */
export function useOwnerIndex(enabled: boolean) {
  const query = useQuery({
    ...REF_OPTIONS,
    queryKey: salesKeys.members(),
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/members", { signal, params: { query: { limit: 200 } } }),
        "members",
      ),
  });
  const byId = new Map<string, string>();
  for (const member of query.data?.items ?? []) byId.set(member.user_id, member.display_name);
  return { byId, isPending: enabled && query.isPending, enabled };
}
